import React from 'react';
import { createPlayerController } from './embedPlayers';

const looksLikeUrl = (str) => typeof str === 'string' && str.match(/^https?:\/\//i);

const extractTikTokId = (url) => {
  const regex = /(?:\/video\/|\/v\/)(\d{10,20})/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

const getYouTubeId = (post) => post.video_id || post.v_id || post.media?.embedId || post.id;

const iframeStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    margin: 0,
    padding: 0,
    background: 'transparent'
};

// Memoized TikTok iframe -- only re-renders when videoId changes (never reloads iframe).
// Always autoplay=1: with autoplay=0 the TikTok player never sends onPlayerReady
// and ignores postMessage commands. Neighbours start muted and the controller
// pauses them as soon as they report playing.
const TikTokEmbed = React.memo(({ videoId, iframeRef }) => (
    <iframe
        ref={iframeRef}
        src={`https://www.tiktok.com/player/v1/${videoId}?controls=0&music_info=0&description=0&closed_caption=0&autoplay=1&muted=1&loop=1`}
        sandbox="allow-scripts allow-same-origin allow-presentation"
        allowFullScreen
        allow="autoplay; fullscreen"
        style={iframeStyle}
        title={`TikTok video ${videoId}`}
    />
), (prev, next) => prev.videoId === next.videoId);

TikTokEmbed.displayName = 'TikTokEmbed';

// Memoized YouTube iframe. Always autoplay=1 for the same reason as TikTok:
// with autoplay=0, YouTube treats our later playVideo() as blocked autoplay and
// keeps pausing itself.
const YouTubeEmbed = React.memo(({ videoId, iframeRef }) => (
    <iframe
        ref={iframeRef}
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&playsinline=1&rel=0&enablejsapi=1&controls=0&modestbranding=1&showinfo=0&iv_load_policy=3&disablekb=1&fs=0&origin=${encodeURIComponent(window.location.origin)}`}
        sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
        allowFullScreen
        allow="autoplay; fullscreen; encrypted-media"
        style={iframeStyle}
        title={`YouTube Short ${videoId}`}
    />
), (prev, next) => prev.videoId === next.videoId);

YouTubeEmbed.displayName = 'YouTubeEmbed';

// Shown instead of an iframe when the post is outside the feed's mount window.
const Poster = ({ src, label }) => (
    <div className="video-poster" aria-label={label}>
        {src && <img src={src} alt="" loading="lazy" />}
    </div>
);

/**
 * Renders a post's media and owns its player controller. Playback and sound
 * are driven by the feed (myJoyNetFeed.js) through the registered controller,
 * so commands can be sent synchronously from user gestures.
 */
const PostMedia = React.memo(({ post, onPlay, registerPlayer, onSoundStatus, shouldMountPlayer }) => {
    const [imgError, setImgError] = React.useState(false);
    const [videoError, setVideoError] = React.useState(false);
    const videoRef = React.useRef(null);
    const iframeRef = React.useRef(null);

    // Callbacks change identity every render; controllers read them via refs.
    const onPlayRef = React.useRef(onPlay);
    const onSoundStatusRef = React.useRef(onSoundStatus);
    onPlayRef.current = onPlay;
    onSoundStatusRef.current = onSoundStatus;

    const tiktokVideoId = React.useMemo(() => {
        if (post.source === 'tiktok' && post.url) {
            return extractTikTokId(post.url);
        }
        return null;
    }, [post.source, post.url]);

    const isEmbed = post.source === 'tiktok' || post.source === 'youtube';
    const mountPlayer = !isEmbed || shouldMountPlayer;

    // Create a controller whenever a player element mounts.
    React.useEffect(() => {
        if (!registerPlayer || !mountPlayer) return;
        const kind = isEmbed ? post.source : 'native';
        const element = isEmbed ? iframeRef.current : videoRef.current;
        const controller = createPlayerController(kind, element, {
            onPlaying: () => { if (onPlayRef.current) onPlayRef.current(); },
            onStatus: (status) => { if (onSoundStatusRef.current) onSoundStatusRef.current(post.id, status); },
        });
        if (!controller) return;
        registerPlayer(post.id, controller);
        return () => {
            registerPlayer(post.id, null);
            controller.destroy();
        };
    }, [post.id, post.source, isEmbed, mountPlayer, registerPlayer, tiktokVideoId, imgError, videoError]);

    const possibleFields = [
        'url', 'image_url', 'video_url', 'media_url', 'thumbnail_url',
        'photo', 'picture', 'media', 'src', 'link', 'file', 'file_url', 'contentUrl', 'content_url'
    ];
    const allUrls = possibleFields
        .map(field => post[field])
        .filter(looksLikeUrl);

    // TikTok embed
    if (post.source === 'tiktok' && post.id) {
        if (tiktokVideoId) {
            return (
                <div className="video-container tiktok-container">
                    {shouldMountPlayer
                        ? <TikTokEmbed videoId={tiktokVideoId} iframeRef={iframeRef} />
                        : <Poster label="TikTok video" />}
                </div>
            );
        }
        return null;
    }

    // YouTube Shorts embed
    if (post.source === 'youtube' && getYouTubeId(post)) {
        const ytVideoId = getYouTubeId(post);
        return (
            <div className="video-container youtube-container">
                {shouldMountPlayer
                    ? <YouTubeEmbed videoId={ytVideoId} iframeRef={iframeRef} />
                    : <Poster src={`https://i.ytimg.com/vi/${ytVideoId}/hq720.jpg`} label="YouTube Short" />}
            </div>
        );
    }

    // Try to render any found URL as image, then as video if image fails
    for (let i = 0; i < allUrls.length; i++) {
        const url = allUrls[i];
        if (!imgError) {
            return (
                <img
                    src={url}
                    alt={post.description || post.text || 'post media'}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onLoad={onPlay}
                    onError={() => setImgError(true)}
                />
            );
        }
        if (imgError && !videoError) {
            return (
                <div className="video-container native-video-container">
                    <video
                        ref={videoRef}
                        src={url}
                        muted
                        playsInline
                        loop
                        style={{ width: '100%', height: '100%', background: '#000', objectFit: 'cover' }}
                        onError={() => setVideoError(true)}
                    />
                </div>
            );
        }
    }

    return null;
}, (prevProps, nextProps) => {
    return (
        prevProps.post.id === nextProps.post.id &&
        prevProps.post.source === nextProps.post.source &&
        prevProps.post.url === nextProps.post.url &&
        prevProps.post.video_id === nextProps.post.video_id &&
        prevProps.post.v_id === nextProps.post.v_id &&
        prevProps.shouldMountPlayer === nextProps.shouldMountPlayer &&
        prevProps.registerPlayer === nextProps.registerPlayer
    );
});

PostMedia.displayName = 'PostMedia';

export default PostMedia;
